// Single source of truth for cookie categories, shared by client components,
// API routes, and the Mongo consent store. No next/headers or mongoose
// imports here so this stays safely importable from both sides.

export type CookieCategoryId = "essential" | "functional" | "analytics" | "advertising";

export type CookieCategory = {
  id: CookieCategoryId;
  label: string;
  description: string;
  required: boolean;
};

export const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: "essential",
    label: "Essential",
    description:
      "Required for sign-in, session security, and remembering your age-gate status. Always on.",
    required: true,
  },
  {
    id: "functional",
    label: "Functional",
    description: "Remembers preferences like your chosen language or UI settings.",
    required: false,
  },
  {
    id: "analytics",
    label: "Analytics",
    description: "Helps us understand how BongGo is used so we can improve it.",
    required: false,
  },
  {
    id: "advertising",
    label: "Advertising",
    description: "Reserved for future ad personalization. Not currently used.",
    required: false,
  },
];

export const ALL_CATEGORY_IDS: CookieCategoryId[] = COOKIE_CATEGORIES.map((c) => c.id);
export const REQUIRED_CATEGORY_IDS: CookieCategoryId[] = COOKIE_CATEGORIES.filter(
  (c) => c.required,
).map((c) => c.id);

// Bump when the consent banner/settings copy materially changes so existing
// records stay tied to the wording the visitor actually agreed to, and so
// stale records trigger a re-prompt.
export const COOKIE_CONSENT_POLICY_VERSION = "v1";

// Fast SSR-readable mirror of the latest consent decision. Not the source of
// truth (the ConsentRecord collection is) — just lets layout.tsx decide
// banner visibility without a DB round trip on every request.
export const CONSENT_COOKIE_NAME = "bonggo.cookie-consent";
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

// Anonymous correlation id for pre-login visitors, reconciled to the user
// row once they sign in. Not signed — it's never used for privileged auth
// decisions, only to link an anonymous consent record to a user later.
export const CONSENT_DEVICE_COOKIE_NAME = "bonggo.consent-device";
export const CONSENT_DEVICE_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export type ConsentCookieValue = {
  policyVersion: string;
  categoriesAccepted: CookieCategoryId[];
};

export const serializeConsentCookieValue = (value: ConsentCookieValue): string =>
  JSON.stringify(value);

// Defensive parse: an untrusted/corrupted cookie should behave like "no
// consent recorded yet" rather than throwing or trusting bad data.
export const parseConsentCookieValue = (raw: string | undefined): ConsentCookieValue | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return null;

    const { policyVersion, categoriesAccepted } = parsed as Record<string, unknown>;
    if (typeof policyVersion !== "string") return null;
    if (!Array.isArray(categoriesAccepted)) return null;

    const validCategories = categoriesAccepted.filter((id): id is CookieCategoryId =>
      ALL_CATEGORY_IDS.includes(id as CookieCategoryId),
    );

    return { policyVersion, categoriesAccepted: validCategories };
  } catch {
    return null;
  }
};
